"use client";

import { useId, type CSSProperties } from "react";

interface VestraBadgeProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * The Vestra company mark — the orange sun on a brushed titanium tile
 * (founder-picked, 10-19), inlined for surfaces that must paint without a
 * network fetch (boot loader, 404, desktop overlays).
 * Its file twin is `public/vestra.svg` (used via img tags / next Image and as
 * the favicon); the body between the GENERATED markers is written by `pnpm brand:sync` — edit brand/vestra.svg, never this block.
 * Each badge scopes the body's ids to itself: the boot loader stacks two, and
 * a second copy of a gradient id would resolve to the first badge's defs.
 *
 * This is the COMPANY mark. The Bash agent identity (chat avatar, notch bolt,
 * logo-mark-*.svg / bash-mark.svg) is a different mark on purpose.
 */
export function VestraBadge({ size, className, style }: VestraBadgeProps) {
  const scope = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const id = (name: string) => `${scope}-${name}`;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* GENERATED-BRAND-MARK:BEGIN (pnpm brand:sync) */}
      <defs>
        <clipPath id={id("vestra-tile")}>
          <rect width="64" height="64" rx="18" />
        </clipPath>
        <linearGradient
          id={id("vestra-light")}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="64"
          y2="64"
        >
          <stop offset="0" stopColor="#FFFFFF" stopOpacity=".4" />
          <stop offset=".3" stopColor="#FFFFFF" stopOpacity=".05" />
          <stop offset=".55" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={id("vestra-shade")}
          gradientUnits="userSpaceOnUse"
          x1="32"
          y1="0"
          x2="32"
          y2="64"
        >
          <stop offset=".62" stopColor="#000000" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity=".6" />
        </linearGradient>
        <linearGradient
          id={id("vestra-metal")}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="64"
          y2="64"
        >
          <stop offset="0" stopColor="#3D3C39" />
          <stop offset=".24" stopColor="#1A1A19" />
          <stop offset=".48" stopColor="#2C2B29" />
          <stop offset=".7" stopColor="#0F0F0F" />
          <stop offset="1" stopColor="#060606" />
        </linearGradient>
        <filter
          id={id("vestra-brush")}
          filterUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="64"
          height="64"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency=".012 .75"
            numOctaves="2"
            seed="7"
          />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="table" tableValues="0 .06" />
          </feComponentTransfer>
        </filter>
        <linearGradient
          id={id("vestra-sun")}
          gradientUnits="userSpaceOnUse"
          x1="32"
          y1="4"
          x2="32"
          y2="60"
        >
          <stop offset="0" stopColor="#FFC06A" />
          <stop offset="1" stopColor="#FF8A52" />
        </linearGradient>
      </defs>
      <rect
        width="64"
        height="64"
        rx="18"
        fill={`url(#${id("vestra-metal")})`}
      />
      <g clipPath={`url(#${id("vestra-tile")})`}>
        <rect width="64" height="64" filter={`url(#${id("vestra-brush")})`} />
        <rect
          x=".45"
          y=".45"
          width="63.1"
          height="63.1"
          rx="17.55"
          fill="none"
          stroke={`url(#${id("vestra-light")})`}
          strokeWidth=".9"
        />
        <rect
          x=".45"
          y=".45"
          width="63.1"
          height="63.1"
          rx="17.55"
          fill="none"
          stroke={`url(#${id("vestra-shade")})`}
          strokeWidth=".9"
        />
      </g>
      <g transform="translate(32 32) scale(.72) translate(-32 -32)">
        <circle cx="32" cy="32" r="11.5" fill={`url(#${id("vestra-sun")})`} />
        <path
          d="M31.85 3.82L32.00 15.20L40.01 7.11M53.94 14.31L45.13 21.53L56.45 22.75M59.51 38.13L48.38 35.74L54.48 45.35M44.36 57.33L39.29 47.14L35.58 57.90M19.90 57.46L24.71 47.14L13.98 50.95M4.56 38.41L15.62 35.74L5.95 29.73M9.87 14.54L18.87 21.53L17.54 10.22"
          fill="none"
          stroke={`url(#${id("vestra-sun")})`}
          strokeWidth="3.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      {/* GENERATED-BRAND-MARK:END */}
    </svg>
  );
}
